import React, { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import BTNLoader from "../../../lib/components/BTNLoader";
import marketplaceService from "../../../service/marketplaceService";
import { UPDATE_QUANTITY_VALIDATION } from "../../../validations/marketplace";
import errorHandler from "../../../utils/errorHandler";

/**
 * B7 — Adjust quantity on an existing capacity grant/purchase (PATCH).
 */
const UpdateQuantityModal = ({ isOpen, onClose, onSuccess, addon }) => {
  const formHook = useForm({
    resolver: yupResolver(UPDATE_QUANTITY_VALIDATION),
    mode: "all",
    defaultValues: { quantity: "", reason: "" },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formHook;

  useEffect(() => {
    if (!isOpen) return;
    reset({
      quantity: addon?.quantity ?? addon?.grantedQuantity ?? "",
      reason: "",
    });
  }, [isOpen, addon, reset]);

  const mutation = useMutation({
    mutationFn: (values) =>
      marketplaceService.updateAddonQuantity(addon.id || addon._id || addon.addonId, {
        quantity: Number(values.quantity),
        reason: values.reason,
      }),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Add-on quantity updated");
      onSuccess?.();
      onClose?.();
    },
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit((values) => mutation.mutate(values)),
      }}
    >
      <div className="d-flex justify-content-between align-items-center gap-3 p-3 pe-2">
        <div>
          <div className="fs-5 fw-medium">Update Quantity</div>
          <div className="text-secondary fs-sm">
            {addon?.name || addon?.itemCode || addon?.code || "Add-on"} — capacity items only
          </div>
        </div>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <Divider />

      <DialogContent>
        <div className="mb-3">
          <label className="d-block mb-1">
            Quantity <span className="text-danger">*</span>
          </label>
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                fullWidth
                size="small"
                error={!!errors.quantity}
                helperText={errors.quantity?.message}
              />
            )}
          />
        </div>
        <div>
          <label className="d-block mb-1">
            Reason <span className="text-danger">*</span>
          </label>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={2}
                placeholder="Customer needs more capacity"
                error={!!errors.reason}
                helperText={errors.reason?.message}
              />
            )}
          />
        </div>
      </DialogContent>

      <DialogActions className="px-3 pb-3">
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending}
          startIcon={<BTNLoader isLoading={mutation.isPending} />}
        >
          Update Quantity
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateQuantityModal;
